import React, { useEffect, useMemo, useState } from "react";
import { publicLink } from "@/lib/landerDomain";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ArrowSquareOut, Eye, PencilSimple, Globe, SealCheck, ListChecks } from "@phosphor-icons/react";
import api from "@/lib/api";
import { FunnelResultsPanel } from "@/components/FunnelResultsPanel";
import { BuyerProfileChanges } from "@/components/BuyerProfileChanges";
import { BuyerWeeklySummaryPanel } from "@/components/BuyerWeeklySummaryPanel";
import { GlobalBuyersPanel } from "@/components/GlobalBuyersPanel";
import { ThankYouSettingsDialog } from "@/components/ThankYouSettingsDialog";
import { DemandMapPanel } from "@/components/demand/DemandMapPanel";
import { PendingTestimonials } from "@/components/testimonials/PendingTestimonials";
import { PageSettingsPanel } from "@/components/pageSettings/PageSettingsPanel";
import { FormRulesDialog } from "@/components/pageSettings/FormRulesDialog";
import { BuyerPagesTab } from "@/components/buyer/BuyerPagesTab";
import { PixelBadge } from "@/components/pixels/PixelBadge";

const BTN = "border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1";

function copyText(text) {
  if (navigator.clipboard) navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
}

function buildFunnelUrl(origin, funnel, { aff, buyer, sub, mb }) {
  const qs = new URLSearchParams();
  if (aff) qs.set("aff", aff);
  if (buyer) qs.set("b", buyer);
  if (sub) qs.set("sub", sub);
  if (mb) qs.set("mb", mb);
  const s = qs.toString();
  return `${origin}/lp/${funnel}${s ? `?${s}` : ""}`;
}

function FunnelCard({ f, origin }) {
  const nav = useNavigate();
  const [tyOpen, setTyOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const url = `${origin}${f.path}`;
  return (
    <div className="border-2 border-black p-4 flex flex-col gap-2 bg-white" data-testid={`global-funnel-card-${f.slug}`}>
      <div className="flex items-center gap-2">
        <span className="font-chivo font-black text-base leading-tight">{f.title}</span>
        <span className="ml-auto"><PixelBadge pageId={f.id} testId={`global-funnel-pixel-${f.slug}`} /></span>
        <span
          className={` text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${f.status === "published" ? "bg-emerald-700 border-emerald-700 text-white" : "bg-red-600 border-red-600 text-white"}`}
          data-testid={`global-funnel-status-${f.slug}`}
        >
          {f.status}
        </span>
      </div>
      <div className="border border-black bg-zinc-50 px-2 py-1.5 text-[10px] font-mono break-all" data-testid={`global-funnel-url-${f.slug}`}>
        {url}
      </div>
      <div className="text-[11px] text-zinc-600 leading-snug truncate" title={f.hero_headline}>
        {f.hero_headline}
      </div>
      <div className="flex flex-wrap gap-2 mt-auto pt-1">
        <button type="button" className={BTN} onClick={() => copyText(url)} data-testid={`global-funnel-copy-${f.slug}`}>
          <Copy size={10} weight="bold" /> Copy
        </button>
        <a className={BTN} href={publicLink(f.path)} target="_blank" rel="noreferrer" data-testid={`global-funnel-open-${f.slug}`}>
          <ArrowSquareOut size={10} weight="bold" /> Open live
        </a>
        <a className={BTN} href={publicLink(`${f.path}?preview=1`)} target="_blank" rel="noreferrer" data-testid={`global-funnel-preview-${f.slug}`}>
          <Eye size={10} weight="bold" /> Preview
        </a>
        <button
          type="button"
          className={`${BTN} !bg-black !text-white hover:!bg-emerald-700`}
          onClick={() => nav(`/admin/landing-pages/custom/${f.id}`)}
          data-testid={`global-funnel-edit-${f.slug}`}
        >
          <PencilSimple size={10} weight="bold" /> Edit shared content
        </button>
        <button type="button" className={BTN} onClick={() => setTyOpen(true)} data-testid={`global-funnel-ty-${f.slug}`}>
          <SealCheck size={10} weight="bold" /> Thank-you page
        </button>
        <button type="button" className={BTN} onClick={() => setRulesOpen(true)} data-testid={`global-funnel-form-rules-${f.slug}`}>
          <ListChecks size={10} weight="bold" /> Form questions
        </button>
      </div>
      {tyOpen && <ThankYouSettingsDialog pageId={f.id} onClose={() => setTyOpen(false)} />}
      {rulesOpen && <FormRulesDialog scope="page" scopeKey={f.id} title={`${f.title} — form questions`} onClose={() => setRulesOpen(false)} />}
    </div>
  );
}

function ApprovalHint({ affObj, slug }) {
  if (!affObj) return null;
  const ok = (affObj.allowed_landing_page_slugs || []).map((s) => s.toLowerCase()).includes(slug);
  return (
    <span
      className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${ok ? "bg-emerald-50 border-emerald-700 text-emerald-900" : "bg-amber-50 border-amber-700 text-amber-900"}`}
      data-testid="global-funnel-builder-approval"
      title={ok ? `Affiliate is approved for ${slug}` : `Approve ${slug} on the affiliate's Manage page or their traffic is rejected`}
    >
      {ok ? "Affiliate approved" : `Affiliate not approved for ${slug}`}
    </span>
  );
}

function FunnelLinkBuilder({ funnels, buyers, affiliates, origin }) {
  const [funnel, setFunnel] = useState("standard");
  const [buyer, setBuyer] = useState("");
  const [aff, setAff] = useState("");
  const [sub, setSub] = useState("");
  const [mb, setMb] = useState("");
  const [mediaBuyers, setMediaBuyers] = useState([]);
  useEffect(() => {
    api.get("/admin/media-buyers/codes").then((r) => setMediaBuyers(r.data.items || [])).catch(() => setMediaBuyers([]));
  }, []);
  const [search, setSearch] = useState("");
  const liveBuyers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return buyers.filter((b) => b.status === "published"
      && (!q || b.code === buyer || `${b.firm_name} ${b.code}`.toLowerCase().includes(q)));
  }, [buyers, search, buyer]);
  const affObj = affiliates.find((a) => a.public_source_id === aff) || null;
  const url = buildFunnelUrl(origin, funnel, { aff, buyer, sub: sub.trim(), mb });

  const pickBuyer = (code) => {
    setBuyer(code);
    const match = buyers.find((b) => b.code === code);
    if (match) setFunnel(match.funnel);
  };

  return (
    <div className="border-t border-black p-4" data-testid="global-funnel-link-builder">
      <div className="label-mono mb-2">Link builder</div>
      <input className="border-2 border-black px-2 py-1.5 text-xs w-full md:w-1/3 mb-2"
             placeholder="Search buyers by firm or code…" value={search}
             onChange={(e) => setSearch(e.target.value)} data-testid="global-funnel-builder-search" />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <select className="border-2 border-black px-2 py-1.5 text-xs bg-white" value={funnel}
                onChange={(e) => setFunnel(e.target.value)} data-testid="global-funnel-builder-funnel">
          {funnels.map((f) => <option key={f.slug} value={f.slug}>{f.title}</option>)}
        </select>
        <select className="border-2 border-black px-2 py-1.5 text-xs bg-white" value={buyer}
                onChange={(e) => pickBuyer(e.target.value)} data-testid="global-funnel-builder-buyer">
          <option value="">No buyer — match from visitor&apos;s answers</option>
          {liveBuyers.map((b) => (
            <option key={b.code} value={b.code}>{b.firm_name}{b.is_sample_page ? " (demo)" : ""} · {b.code}</option>
          ))}
        </select>
        <select className="border-2 border-black px-2 py-1.5 text-xs bg-white" value={aff}
                onChange={(e) => setAff(e.target.value)} data-testid="global-funnel-builder-vendor">
          <option value="">No affiliate (direct traffic)</option>
          {affiliates.filter((a) => a.public_source_id).map((a) => (
            <option key={a.id} value={a.public_source_id}>{a.name} · {a.public_source_id}</option>
          ))}
        </select>
        <input className="border-2 border-black px-2 py-1.5 text-xs" placeholder="sub id (optional)"
               value={sub} onChange={(e) => setSub(e.target.value)} data-testid="global-funnel-builder-sub" />
        <select className="border-2 border-black px-2 py-1.5 text-xs bg-white md:col-span-2" value={mb}
                onChange={(e) => setMb(e.target.value)} data-testid="global-funnel-builder-media-buyer">
          <option value="">Don't credit a media buyer</option>
          {mediaBuyers.map((m) => <option key={m.id} value={m.tracking_code}>Credit {m.name} · {m.tracking_code}</option>)}
        </select>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className="flex-1 border border-black bg-zinc-50 px-2 py-1.5 text-[11px] font-mono break-all"
             data-testid="global-funnel-builder-url">{url}</div>
        <ApprovalHint affObj={affObj} slug={buyer || funnel} />
        <button type="button" className={BTN} onClick={() => copyText(url)} data-testid="global-funnel-builder-copy">
          <Copy size={10} weight="bold" /> Copy
        </button>
      </div>
      <div className="text-[10.5px] text-zinc-600 mt-2 leading-relaxed">
        {buyer
          ? "This link shows the buyer's company details while the visitor's state (from their location, then the form) fits that buyer and they're under their caps. Otherwise the page switches to the next matching buyer in rotation, or to the generic page and the lead goes to the marketplace."
          : "No buyer code: the page shows the buyer that takes the visitor's state (from their location as the page opens, then from the form). Several matching buyers rotate evenly or by the % share you set; with no match the page stays generic and the lead goes to the marketplace."}
      </div>
    </div>
  );
}

export const GlobalFunnelsSection = ({ affiliates = [], origin = "" }) => {
  const [data, setData] = useState({ funnels: [], buyers: [], loaded: false });
  const [tab, setTab] = useState("funnels");

  const load = () => {
    api.get("/admin/global-funnels")
      .then((r) => setData({ funnels: r.data?.funnels || [], buyers: r.data?.buyers || [], loaded: true }))
      .catch(() => setData((d) => ({ ...d, loaded: true })));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="brut-card mb-6" data-testid="global-funnels-section">
      <div className="px-4 py-3 border-b border-black flex items-center gap-3 flex-wrap">
        <Globe size={16} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter text-sm">Global funnels</div>
        <div className="label-mono text-zinc-600">
          One shared page set for every buyer · company details load from the buyer code (?b=)
        </div>
        <div className="ml-auto flex border-2 border-black" data-testid="global-funnels-tabs">
          {[["funnels", "Funnels & buyers"], ["buyer_pages", "Buyer pages"], ["settings", "Page settings"], ["demand", "Demand map"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`global-funnels-tab-${k}`}
                    className={`px-3 py-1 text-[10px] uppercase tracking-widest border-r-2 border-black last:border-r-0 ${tab === k ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {tab === "demand" && <DemandMapPanel />}
      {tab === "buyer_pages" && <BuyerPagesTab />}
      {tab === "settings" && <PageSettingsPanel />}
      {tab === "funnels" && !data.loaded && <div className="p-4 label-mono" data-testid="global-funnels-loading">Loading…</div>}
      {tab === "funnels" && data.loaded && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4" data-testid="global-funnels-grid">
            {data.funnels.map((f) => <FunnelCard key={f.slug} f={f} origin={origin} />)}
          </div>
          <FunnelLinkBuilder funnels={data.funnels} buyers={data.buyers} affiliates={affiliates} origin={origin} />
          <PendingTestimonials />
          <GlobalBuyersPanel buyers={data.buyers} onChanged={load} />
          <BuyerProfileChanges onReviewed={load} />
          <BuyerWeeklySummaryPanel />
          <div className="px-4 pb-4">
            <FunnelResultsPanel
              endpoint="/admin/global-funnels/results"
              testIdPrefix="admin-funnel-results"
              title="Results by buyer"
              subtitle="Visits, leads and deliveries per shared funnel and buyer"
            />
          </div>
        </>
      )}
    </div>
  );
};
